// src/components/Estanques/CrearEstanque.jsx
import React, { useEffect, useState } from "react";
import estanqueService from "../../services/estanqueService";
import usuarioService from "../../services/usuarioService";

// Util por si más adelante agregas validaciones extra
const isNonEmpty = (v) => String(v ?? "").trim().length > 0;

/** Normaliza cualquier respuesta a un arreglo */
const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data?.data && Array.isArray(data.data)) return data.data;
  if (data?.items && Array.isArray(data.items)) return data.items;
  if (data?.usuarios && Array.isArray(data.usuarios)) return data.usuarios;
  return [];
};

const CrearEstanque = ({ onCreated }) => {
  // Form
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");

  // Permisos por usuario
  const [usuarios, setUsuarios] = useState([]); // ← listado (siempre arreglo)
  const [usuariosSeleccionados, setUsuariosSeleccionados] = useState([]);

  // Ui state
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  // Carga inicial
  useEffect(() => {
    (async () => {
      try {
        const resUsuarios = await usuarioService.obtenerUsuariosCreadosPorAdmin();
        setUsuarios(toArray(resUsuarios)); // ← siempre arreglo
      } catch (err) {
        console.error("Error cargando usuarios:", err);
        setUsuarios([]); // fallback seguro
        setError("No se pudieron cargar los usuarios para permisos.");
      }
    })();
  }, []);

  // Toggle de permisos
  const togglePermiso = (id) => {
    setUsuariosSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Guardar
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje("");
    setError("");

    if (!isNonEmpty(nombre) || !isNonEmpty(descripcion)) {
      setError("Completa nombre y descripción.");
      return;
    }

    const payload = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim(),
      permisos: usuariosSeleccionados, // ids de usuarios con acceso
    };

    try {
      setGuardando(true);
      const creado = await estanqueService.crearEstanque(payload);
      onCreated?.({ ...payload, id: creado.id });
      setMensaje("✅ Estanque creado con éxito.");
      // limpiar
      setNombre("");
      setDescripcion("");
      setUsuariosSeleccionados([]);
    } catch (err) {
      console.error("Error al crear estanque:", err);
      const status = err?.response?.status;
      if (status === 401) setError("No autorizado. Inicia sesión nuevamente.");
      else if (status === 403) setError("No tienes permisos para crear estanques.");
      else setError(err?.response?.data || "Error al crear el estanque.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="bg-neutral-800 p-4 sm:p-6 rounded-lg shadow-md w-full max-w-2xl mx-auto">
      <h3 className="text-green-400 text-lg sm:text-xl font-semibold mb-4">
        Crear Estanque
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-white mb-1 text-sm sm:text-base">Nombre</label>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            placeholder="Ej. Estanque #1"
          />
        </div>

        <div>
          <label className="block text-white mb-1 text-sm sm:text-base">Descripción</label>
          <input
            type="text"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            placeholder="Ej. Estanque norte, 15,000 L"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm sm:text-base">
            Permisos (usuarios que podrán ver este estanque)
          </label>
          <div className="max-h-60 overflow-y-auto border border-neutral-600 p-2 rounded bg-neutral-900">
            {usuarios.length > 0 ? (
              usuarios.map((u) => (
                <label key={u.id} className="flex items-center text-white text-sm py-1">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={usuariosSeleccionados.includes(u.id)}
                    onChange={() => togglePermiso(u.id)}
                  />
                  {u.nombre} ({u.email})
                </label>
              ))
            ) : (
              <p className="text-neutral-400 text-sm">
                No hay usuarios temporales disponibles.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={guardando}
            className={`w-full sm:w-auto text-white px-5 py-2.5 rounded ${
              guardando ? "bg-gray-600" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {guardando ? "Guardando..." : "Guardar Estanque"}
          </button>
        </div>
      </form>

      {mensaje && <p className="text-green-400 mt-4">{mensaje}</p>}
      {error && <p className="text-red-400 mt-4">{error}</p>}
    </div>
  );
};

export default CrearEstanque;
